// ── Report regenerate section — SA-only, LIVE POST (FR-REPORT-003) ──────────
// POST /sessions/:session_id/reports/regenerate is NOW LIVE. regenerate.reports
// (migration 0225) grants this to super_admin ONLY — the section renders for NO
// other role (the FR-REPORT-003 gate; the biting test asserts ABSENCE for coach /
// company_admin / cohort_lead / admin / participant). With a generated UUID
// Idempotency-Key the SA triggers regeneration; while the report-agent lane is
// closed the response is an HONEST pending/queued (report:null, generated:false)
// — it NEVER fabricates a generated report. A denied/closed response renders its
// honest message; the toast fires only AFTER the backend confirms.

import { Alert, Button, Module, ModuleBody, ModuleHead, useToast } from "@vjal/ui";
import { RefreshCw } from "lucide-react";

import { useReportRegenerateMutation } from "../../../hooks/useCoachingReportGeneration";
import { canRegenerateReports } from "../reports-helpers";
import { describeReportWriteError } from "../reports-write";
import { ReportEnqueueResult } from "../report-enqueue-result";
import type { CoachingRole } from "../../../domain/roles";

export function ReportRegenerateSection({ role, sessionId }: { role: CoachingRole; sessionId: string | null | undefined }) {
  const toast = useToast();
  const regenerate = useReportRegenerateMutation();

  // SA-ONLY: never render for any other role.
  if (!canRegenerateReports(role)) return null;

  const validSession = typeof sessionId === "string" && sessionId.trim().length > 0;
  const writeError = describeReportWriteError(regenerate.error);

  function run() {
    if (!validSession) return;
    regenerate.reset();
    regenerate.mutate(
      { sessionId: sessionId as string },
      { onSuccess: () => toast.success("Regeneration queued") }
    );
  }

  return (
    <Module span={4}>
      <ModuleHead title="Regenerate" subtitle="Super-admin maintenance action." />
      <ModuleBody className="space-y-3" data-testid="report-regenerate">
        <Button
          type="button"
          variant="secondary"
          leftIcon={<RefreshCw size={16} />}
          disabled={!validSession}
          loading={regenerate.isPending}
          onClick={run}
          aria-label="Regenerate report"
        >
          Regenerate report
        </Button>
        {!validSession ? (
          <Alert variant="info">This report has no linked session, so it can't be regenerated.</Alert>
        ) : null}
        <ReportEnqueueResult result={regenerate.data ?? null} />
        {writeError ? <Alert variant={writeError.tone}>{writeError.message}</Alert> : null}
      </ModuleBody>
    </Module>
  );
}
